import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyleWide, FormRow, DetailField, fmtMoney, fmtQty } from './ProcurementShared';

export default function ProcurementSupplierInvoices() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/procurement/supplier-invoices?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  const fetchPos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/procurement/purchase-orders?per_page=200`, { headers: getAuthHeaders() });
      const json = await res.json();
      setPos(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); }
  }, [companyId]);

  useEffect(() => { fetchList(); fetchPos(); }, [fetchList, fetchPos]);

  const viewItem = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/supplier-invoices/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  const registerItem = async (id, override = false) => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/supplier-invoices/${id}/register`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ override_difference: override }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      if (!override && j.message && j.message.includes('differences')) {
        if (window.confirm(j.message + '\n\nRegister anyway (override)?')) return registerItem(id, true);
      } else alert(j.message || 'Register failed');
      return;
    }
    fetchList(); if (viewing) viewItem(id);
  };

  const cancelItem = async (id) => {
    const reason = window.prompt('Cancellation reason (required):');
    if (!reason) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/supplier-invoices/${id}/cancel`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cancel failed'); return; }
    fetchList(); if (viewing) viewItem(id);
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>💳 Supplier Invoices & Payables</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Supplier Invoice</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search reference/invoice #..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 180 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option><option value="registered">Registered</option>
          <option value="partially_paid">Partially Paid</option><option value="paid">Paid</option><option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Reference', 'Supplier Invoice #', 'PO #', 'Total', 'Remaining', 'Match', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No supplier invoices</td></tr>}
              {list.map(inv => (
                <tr key={inv.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}><a href="#view" onClick={e => { e.preventDefault(); viewItem(inv.id); }} style={{ color: '#3b82f6' }}>{inv.internal_reference}</a></td>
                  <td style={tdStyle}>{inv.supplier_invoice_number || '—'}</td>
                  <td style={tdStyle}>{inv.po?.po_number || '—'}</td>
                  <td style={tdStyle}>{fmtMoney(inv.total)}</td>
                  <td style={tdStyle}>{fmtMoney(inv.remaining_amount)}</td>
                  <td style={tdStyle}><Badge value={inv.match_status} /></td>
                  <td style={tdStyle}><Badge value={inv.status} /></td>
                  <td style={tdStyle}>
                    {inv.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => registerItem(inv.id)}>Register</button>}
                    {inv.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => cancelItem(inv.id)}>Cancel</button>}
                    <button style={btnSmall} onClick={() => viewItem(inv.id)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <InvoiceModal companyId={companyId} pos={pos} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
      {viewing && <InvoiceDetail companyId={companyId} invoice={viewing} onClose={() => setViewing(null)} onChanged={() => { viewItem(viewing.id); fetchList(); }} onRegister={registerItem} onCancel={cancelItem} />}
    </div>
  );
}

function InvoiceModal({ companyId, pos, onClose, onSaved }) {
  const [poId, setPoId] = useState('');
  const [poDetail, setPoDetail] = useState(null);
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState({ supplier_invoice_number: '', invoice_date: '', due_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!poId) { setPoDetail(null); setLines([]); return; }
    fetch(`${API}/companies/${companyId}/procurement/purchase-orders/${poId}`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(po => {
        setPoDetail(po);
        setLines((po.lines || []).map(l => ({ po_line_id: l.id, description: l.description, quantity: l.quantity, unit_cost: l.unit_cost })));
      });
  }, [poId, companyId]);

  const updateLine = (i, field, val) => { const next = [...lines]; next[i] = { ...next[i], [field]: val }; setLines(next); };

  const total = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0);

  const save = async () => {
    if (!poId) { alert('Select a PO'); return; }
    const validLines = lines.filter(l => l.description && Number(l.quantity) > 0);
    if (validLines.length === 0) { alert('At least one line item is required'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/procurement/supplier-invoices`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, po_id: Number(poId), lines: validLines }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Supplier Invoice</h3>
        <FormRow label="Purchase Order *">
          <select style={inputStyle} value={poId} onChange={e => setPoId(e.target.value)}>
            <option value="">Select PO...</option>
            {pos.map(po => <option key={po.id} value={po.id}>{po.po_number} — {po.supplier?.name}</option>)}
          </select>
        </FormRow>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Supplier Invoice #"><input style={inputStyle} value={form.supplier_invoice_number} onChange={e => setForm({ ...form, supplier_invoice_number: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Invoice Date"><input type="date" style={inputStyle} value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Due Date"><input type="date" style={inputStyle} value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></FormRow></div>
        </div>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>

        {poDetail && (
          <>
            <h4>Lines (pre-filled from PO — edit to match the supplier's actual invoice)</h4>
            <table style={tableStyle}>
              <thead><tr>{['Description', 'Qty', 'Unit Cost', 'Line Total'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td style={tdStyle}><input style={inputStyle} value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} /></td>
                    <td style={{ ...tdStyle, width: 80 }}><input type="number" style={inputStyle} value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} /></td>
                    <td style={{ ...tdStyle, width: 100 }}><input type="number" style={inputStyle} value={l.unit_cost} onChange={e => updateLine(i, 'unit_cost', e.target.value)} /></td>
                    <td style={tdStyle}>{fmtMoney((Number(l.quantity) || 0) * (Number(l.unit_cost) || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ textAlign: 'right', marginTop: 10, fontWeight: 700 }}>Total: {fmtMoney(total)}</div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceDetail({ companyId, invoice, onClose, onChanged, onRegister, onCancel }) {
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('');
  const [ccAmount, setCcAmount] = useState('');
  const [ccApplication, setCcApplication] = useState('reduce_payable');

  const recordPayment = async () => {
    if (!payAmount || Number(payAmount) <= 0) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/supplier-invoices/${invoice.id}/payments`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: Number(payAmount), payment_method: payMethod }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Payment failed'); return; }
    setPayAmount(''); setPayMethod(''); onChanged();
  };

  const addCreditNote = async () => {
    if (!ccAmount || Number(ccAmount) <= 0) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/supplier-invoices/${invoice.id}/credit-notes`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: Number(ccAmount), application: ccApplication }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Credit note failed'); return; }
    setCcAmount(''); onChanged();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{invoice.internal_reference} <Badge value={invoice.status} /> <Badge value={invoice.match_status} /></h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
          <DetailField label="Supplier" value={invoice.supplier?.name} />
          <DetailField label="PO #" value={invoice.po?.po_number} />
          <DetailField label="Supplier Invoice #" value={invoice.supplier_invoice_number} />
          <DetailField label="Total" value={fmtMoney(invoice.total)} />
          <DetailField label="Paid" value={fmtMoney(invoice.paid_amount)} />
          <DetailField label="Remaining" value={fmtMoney(invoice.remaining_amount)} />
        </div>

        {invoice.match_status === 'difference_found' && (invoice.match_details || []).length > 0 && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12, color: '#991b1b' }}>
            <b>Matching Differences:</b>
            <ul style={{ margin: '6px 0 0 18px' }}>{invoice.match_details.map((d, i) => <li key={i}>{d}</li>)}</ul>
          </div>
        )}

        <h4>Line Items</h4>
        <table style={tableStyle}>
          <thead><tr>{['Description', 'Qty', 'Unit Cost', 'Line Total'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {(invoice.lines || []).map(l => (
              <tr key={l.id}><td style={tdStyle}>{l.description}</td><td style={tdStyle}>{fmtQty(l.quantity)}</td><td style={tdStyle}>{fmtMoney(l.unit_cost)}</td><td style={tdStyle}>{fmtMoney(l.line_total)}</td></tr>
            ))}
          </tbody>
        </table>

        {(invoice.payments || []).length > 0 && (
          <>
            <h4>Payments</h4>
            <table style={tableStyle}>
              <thead><tr>{['Date', 'Amount', 'Method', 'Remaining After'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {invoice.payments.map(p => (
                  <tr key={p.id}><td style={tdStyle}>{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '—'}</td><td style={tdStyle}>{fmtMoney(p.amount)}</td><td style={tdStyle}>{p.payment_method || '—'}</td><td style={tdStyle}>{fmtMoney(p.remaining_after)}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {['registered', 'partially_paid'].includes(invoice.status) && (
          <>
            <h4>Record Payment</h4>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <input type="number" style={inputStyle} placeholder="Amount" value={payAmount} onChange={e => setPayAmount(e.target.value)} />
              <input style={inputStyle} placeholder="Method" value={payMethod} onChange={e => setPayMethod(e.target.value)} />
              <button style={btnSecondary} onClick={recordPayment}>Record</button>
            </div>

            <h4>Add Credit Note</h4>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <input type="number" style={inputStyle} placeholder="Amount" value={ccAmount} onChange={e => setCcAmount(e.target.value)} />
              <select style={inputStyle} value={ccApplication} onChange={e => setCcApplication(e.target.value)}>
                <option value="reduce_payable">Reduce Payable</option>
                <option value="offset_future_invoice">Offset Future Invoice</option>
                <option value="refund_receivable">Refund Receivable</option>
              </select>
              <button style={btnSecondary} onClick={addCreditNote}>Add</button>
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          {invoice.status === 'draft' && <button style={btnPrimary} onClick={() => onRegister(invoice.id)}>Register</button>}
          {invoice.status === 'draft' && <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => onCancel(invoice.id)}>Cancel</button>}
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
